<script setup>
/* global axios */
import { computed, watch, onMounted, ref } from 'vue';
import {
  useMapGetter,
  useFunctionGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { KANBAN_MODULE_PERMISSION_BY_KEY } from 'dashboard/constants/permissions.js';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';
import ContactInfo from './contact/ContactInfo.vue';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationInfo from './ConversationInfo.vue';
import MetaCapiConversionBox from 'dashboard/components/widgets/conversation/MetaCapiConversionBox.vue';
import ConversationKanbanBox from './ConversationKanbanBox.vue';
import ConversationInternalChatBox from './ConversationInternalChatBox.vue';
import ConversationGroupBox from './ConversationGroupBox.vue';
import ConversationScheduledBox from './ConversationScheduledBox.vue';
import ConversationFlowBox from './ConversationFlowBox.vue';
import ConversationAppointmentBox from './ConversationAppointmentBox.vue';
import ConversationWalletBox from './ConversationWalletBox.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import SharedFiles from './SharedFiles.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';
import ShopifyOrdersList from 'dashboard/components/widgets/conversation/ShopifyOrdersList.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import LinearIssuesList from 'dashboard/components/widgets/conversation/linear/IssuesList.vue';
import LinearSetupCTA from 'dashboard/components/widgets/conversation/linear/LinearSetupCTA.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);
// Meta CAPI: o item do box só entra na lista (draggable/recolhível) se a conta
// configurou a integração. Checagem account-level (não por conversa).
const metaCapiConfigured = ref(false);

const shopifyIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'shopify'
);

const isShopifyFeatureEnabled = computed(
  () => shopifyIntegration.value.enabled
);

const { isCloudFeatureEnabled, currentAccount, accountId } = useAccount();

const loadMetaCapiConfigured = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/meta_capi_config`
    );
    metaCapiConfigured.value = data.data?.configured || false;
  } catch (e) {
    metaCapiConfigured.value = false;
  }
};
const { checkPermissions } = usePolicy();
const moduleAllowed = mod =>
  checkPermissions([
    'administrator',
    'agent',
    KANBAN_MODULE_PERMISSION_BY_KEY[mod],
  ]);
const kanbanEnabled = computed(
  () =>
    currentAccount.value?.feature_modules?.kanban !== false &&
    moduleAllowed('kanban')
);
const internalChatEnabled = computed(
  () =>
    currentAccount.value?.feature_modules?.internal_chat !== false &&
    moduleAllowed('internal_chat')
);
const scheduledEnabled = computed(
  () => currentAccount.value?.feature_modules?.scheduled_messages !== false
);
const flowsEnabled = computed(
  () =>
    currentAccount.value?.feature_modules?.flows !== false &&
    moduleAllowed('flows')
);
const appointmentsEnabled = computed(
  () =>
    currentAccount.value?.feature_modules?.appointments !== false &&
    moduleAllowed('appointments')
);
const walletEnabled = computed(
  () =>
    currentAccount.value?.feature_modules?.wallet !== false &&
    moduleAllowed('wallet')
);

const isLinearFeatureEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.LINEAR)
);

const linearIntegration = useFunctionGetter(
  'integrations/getIntegration',
  'linear'
);

const isLinearClientIdConfigured = computed(() => {
  return !!linearIntegration.value?.id;
});

const isLinearConnected = computed(
  () => linearIntegration.value?.enabled || false
);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value.additional_attributes || {}
);
// Ticket de GRUPO (WhatsApp): tem o JID do grupo nos atributos da conversa.
// O group_jid fica no additional_attributes do OBJETO da conversa (currentChat),
// não no metadata (browser/referer). Cobre as duas fontes por garantia.
const isGroupConversation = computed(
  () =>
    !!(
      currentChat.value?.additional_attributes?.group_jid ||
      conversationAdditionalAttributes.value.group_jid
    )
);
// nº de pedidos aguardando aprovação (emitido pelo box) — vira badge no título
const groupRequestCount = ref(0);
// o box de grupo abre por padrão (p/ o pedido não passar batido); respeita o toggle
const groupBoxOpen = ref(true);

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(contactId, (newContactId, prevContactId) => {
  if (newContactId && newContactId !== prevContactId) {
    getContactDetails();
  }
});

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  getContactDetails();
  loadMetaCapiConfigured();
  store.dispatch('attributes/get', 0);
  // Load integrations to ensure linear integration state is available
  store.dispatch('integrations/get', 'linear');
});
</script>

<template>
  <div class="w-full">
    <SidebarActionsHeader
      :title="$t('CONVERSATION.SIDEBAR.CONTACT')"
      @close="closeContactPanel"
    />
    <ContactInfo :contact="contact" :channel-type="channelType" />
    <div class="px-2 pb-8 list-group">
      <!-- Ticket de GRUPO: painel de aprovações (fila de entrada + modo aprovação).
           Abre por padrão e mostra a contagem no título p/ o pedido não passar batido. -->
      <div v-if="isGroupConversation" class="mb-3">
        <AccordionItem
          :title="
            groupRequestCount > 0
              ? `${$t('KANBAN.groupApproval.boxTitle')} (${groupRequestCount})`
              : $t('KANBAN.groupApproval.boxTitle')
          "
          :is-open="groupBoxOpen"
          compact
          @toggle="value => (groupBoxOpen = value)"
        >
          <ConversationGroupBox
            :conversation-id="conversationId"
            @count="value => (groupRequestCount = value)"
          />
        </AccordionItem>
      </div>
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        class="flex flex-col gap-3"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @toggle="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <ConversationAction
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'kanban' && kanbanEnabled">
            <AccordionItem
              :title="$t('KANBAN.menuTitle')"
              :is-open="isContactSidebarItemOpen('is_kanban_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_kanban_open', value)"
            >
              <ConversationKanbanBox :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'internal_chat' && internalChatEnabled"
          >
            <AccordionItem
              title="Chat Interno"
              :is-open="isContactSidebarItemOpen('is_internal_chat_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_internal_chat_open', value)
              "
            >
              <ConversationInternalChatBox :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'scheduled_messages' && scheduledEnabled
            "
          >
            <AccordionItem
              :title="$t('KANBAN.scheduled.boxTitle')"
              :is-open="isContactSidebarItemOpen('is_kanban_scheduled_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_kanban_scheduled_open', value)
              "
            >
              <ConversationScheduledBox :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'flow' && flowsEnabled">
            <AccordionItem
              :title="$t('CHATBOT.flowBox.title')"
              :is-open="isContactSidebarItemOpen('is_kanban_flow_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_kanban_flow_open', value)
              "
            >
              <ConversationFlowBox :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'appointment' && appointmentsEnabled"
          >
            <AccordionItem
              :title="$t('KANBAN.appointmentBox.boxTitle')"
              :is-open="isContactSidebarItemOpen('is_kanban_appointment_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_kanban_appointment_open', value)
              "
            >
              <ConversationAppointmentBox :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'wallet' && walletEnabled">
            <AccordionItem
              :title="$t('WALLET.box.title')"
              :is-open="isContactSidebarItemOpen('is_wallet_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_wallet_open', value)"
            >
              <ConversationWalletBox :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <AccordionItem
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @toggle="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <ConversationParticipant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <ConversationInfo
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'meta_capi' && metaCapiConfigured">
            <AccordionItem
              :title="$t('META_CAPI.BOX.TITLE')"
              :is-open="isContactSidebarItemOpen('is_meta_capi_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_meta_capi_open', value)
              "
            >
              <MetaCapiConversionBox :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @toggle="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <CustomAttributes
                attribute-type="contact_attribute"
                attribute-from="conversation_contact_panel"
                :contact-id="contact.id"
                :empty-state-message="
                  $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                "
              />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <AccordionItem
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <ContactConversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </AccordionItem>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @toggle="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <MacrosList :conversation-id="conversationId" />
            </AccordionItem>
          </woot-feature-toggle>
          <div
            v-else-if="
              element.name === 'linear_issues' &&
              isLinearFeatureEnabled &&
              isLinearClientIdConfigured
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES')"
              :is-open="isContactSidebarItemOpen('is_linear_issues_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_linear_issues_open', value)
              "
            >
              <LinearSetupCTA v-if="!isLinearConnected" />
              <LinearIssuesList v-else :conversation-id="conversationId" />
            </AccordionItem>
          </div>
          <div
            v-else-if="
              element.name === 'shopify_orders' && isShopifyFeatureEnabled
            "
          >
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS')"
              :is-open="isContactSidebarItemOpen('is_shopify_orders_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shopify_orders_open', value)
              "
            >
              <ShopifyOrdersList :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'contact_notes'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES')"
              :is-open="isContactSidebarItemOpen('is_contact_notes_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_contact_notes_open', value)
              "
            >
              <ContactNotes :contact-id="contactId" />
            </AccordionItem>
          </div>
          <div v-else-if="element.name === 'shared_files'">
            <AccordionItem
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.SHARED_FILES')"
              :is-open="isContactSidebarItemOpen('is_shared_files_open')"
              compact
              @toggle="
                value => toggleSidebarUIState('is_shared_files_open', value)
              "
            >
              <SharedFiles />
            </AccordionItem>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.contact--profile) {
  @apply pb-3 border-b border-solid border-n-weak;
}
</style>
